import { routes } from "@/router";
import type { ProductPriceDto } from "@advanced-sales-technologies/backend-client/models/ProductPriceDto";
import { useTranslation } from "react-i18next";
import { HiOutlinePlus } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import { useCheckout } from "@/hooks/api/subscription/useCheckout";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";
import { ModalCloseProps } from "@/components/molecules/Modal";

type Props = {
  interval: "month" | "year";
  subscription: ProductPriceDto;
} & ModalCloseProps;

const ChangePaymentIntervalModal = ({ onClose, preventClose = false, interval, subscription }: Props) => {
  const { t } = useTranslation();
  const { getCheckoutUrlAsync, isPending } = useCheckout();
  const navigate = useNavigate();

  const onSubmit = async () => {
    const response = await getCheckoutUrlAsync({ price_id: subscription.id });
    if (response.url) {
      // Handle payment via Stripe
      window.location.replace(response.url);
    } else {
      // Handle payment in background and redirect to success page
      navigate(routes.paymentSuccess);
    }
  };

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlinePlus} size={"lg"} color={"primary"} />}
      heading={t(`settings.plans.update.interval.heading`)}
      preventClose={preventClose}
      body={t(`settings.plans.update.interval.description`)}
      onClose={onClose}
    >
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button type={"button"} label={t("cancel")} onClick={() => onClose(false)} color="natural" variants="outline" />
        <Button
          type={"button"}
          label={t(`settings.plans.update.interval.button`, {
            interval:
              interval === "month"
                ? t("settings.plans.monthly").toLowerCase()
                : t("settings.plans.annually").toLowerCase(),
          })}
          onClick={() => onSubmit()}
          isLoading={isPending}
          color="brand"
          variants="primary"
        />
      </div>
    </BasicModalContent>
  );
};

export default ChangePaymentIntervalModal;
